// src/pages/admin/Page_Leads/LeadsBoard.js
//
// F6 M2 - Tabla del panel comercial de leads (antes contenido directo de
// Page_Leads). Extraida como componente para vivir dentro de la pestana
// "Leads" de la pagina contenedora (Page_Leads con tabs).
//
// Tabla con paginacion + filtros (vertical, status, search, fecha, asignado).
// Click en una fila navega al detalle (M3). Cambios de status via dropdown
// directo en la fila (UX rapida).

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Input, Select, DatePicker, Tag, Tooltip, message } from "antd";
import { ReloadOutlined, SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useAuth } from "../../../hooks/useAuth";
import { leadsApi } from "../../../api/leadsApi";
import "./Page_Leads.scss";

const { RangePicker } = DatePicker;

// Status -> color del Tag AntD. Alineado con statuses del backend (Lead.js).
const STATUS_COLOR = {
  new: "blue",
  contacted: "cyan",
  qualified: "geekblue",
  demo_scheduled: "purple",
  proposal_sent: "gold",
  converted: "green",
  discarded: "default",
};

const STATUS_LABEL = {
  new: "Nuevo",
  contacted: "Contactado",
  qualified: "Cualificado",
  demo_scheduled: "Demo agendada",
  proposal_sent: "Propuesta enviada",
  converted: "Convertido",
  discarded: "Descartado",
};

const STATUS_OPTIONS = Object.entries(STATUS_LABEL).map(([value, label]) => ({
  value,
  label,
}));

const VERTICAL_OPTIONS = [
  { value: "educa", label: "Educa360" },
  { value: "revi", label: "REVI" },
  { value: "other", label: "Otros" },
];

const ASSIGNED_OPTIONS = [
  { value: "mine", label: "Mis leads" },
  { value: "unassigned", label: "Sin asignar" },
];

export function LeadsBoard() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();

  const [data, setData] = useState({ items: [], total: 0, byStatus: {} });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const [filters, setFilters] = useState({
    vertical: undefined,
    status: undefined,
    assigned_to: undefined,
    search: "",
    from: undefined,
    to: undefined,
  });

  const fetchLeads = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    try {
      const res = await leadsApi.list(accessToken, {
        page,
        pageSize,
        vertical: filters.vertical,
        status: filters.status,
        assigned_to: filters.assigned_to,
        search: filters.search || undefined,
        from: filters.from,
        to: filters.to,
      });
      setData(res.data || { items: [], total: 0, byStatus: {} });
    } catch (err) {
      message.error(`Error cargando leads: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, pageSize, filters]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // F6 M5 - Carga inicial de users asignables (admins).
  useEffect(() => {
    if (!accessToken) return;
    leadsApi
      .listAssignableUsers(accessToken)
      .then((res) => setAssignableUsers(res?.data?.items || []))
      .catch(() => {
        // Silencioso: la tabla sigue funcionando sin asignacion.
      });
  }, [accessToken]);

  const handleStatusChange = useCallback(
    async (leadId, newStatus) => {
      try {
        await leadsApi.patch(accessToken, leadId, { status: newStatus });
        message.success(`Estado actualizado a ${STATUS_LABEL[newStatus]}`);
        fetchLeads();
      } catch (err) {
        message.error(`Error: ${err.message}`);
      }
    },
    [accessToken, fetchLeads],
  );

  const handleAssignChange = useCallback(
    async (leadId, userIdOrNull) => {
      try {
        await leadsApi.patch(accessToken, leadId, {
          assigned_to: userIdOrNull,
        });
        message.success(userIdOrNull ? "Lead asignado" : "Lead desasignado");
        fetchLeads();
      } catch (err) {
        message.error(`Error: ${err.message}`);
      }
    },
    [accessToken, fetchLeads],
  );

  const assignOptions = useMemo(
    () => [
      { value: "", label: "Sin asignar" },
      ...assignableUsers.map((u) => ({
        value: u._id,
        label: u.name || u.email,
      })),
    ],
    [assignableUsers],
  );

  const columns = useMemo(
    () => [
      {
        title: "Fecha",
        dataIndex: "created_at",
        key: "created_at",
        width: 110,
        render: (v) => (
          <Tooltip title={dayjs(v).format("YYYY-MM-DD HH:mm")}>
            <span>{dayjs(v).format("DD/MM/YYYY")}</span>
          </Tooltip>
        ),
      },
      {
        title: "Vertical",
        dataIndex: "vertical",
        key: "vertical",
        width: 90,
        render: (v) => <Tag>{v || "—"}</Tag>,
      },
      {
        title: "Nombre / Email",
        key: "contact",
        render: (_, row) => (
          <div>
            <div style={{ fontWeight: 600 }}>{row.name || "—"}</div>
            <div style={{ color: "#64748b", fontSize: 12 }}>{row.email}</div>
          </div>
        ),
      },
      {
        title: "Centro",
        dataIndex: "centerName",
        key: "centerName",
        render: (v) => v || <span style={{ color: "#94a3b8" }}>—</span>,
      },
      {
        // Plan adquirido por la cuenta asociada al lead (cruce por email en
        // el backend). Sin cuenta -> "Sin cuenta" gris.
        title: "Plan",
        dataIndex: "plan",
        key: "plan",
        width: 120,
        render: (plan, row) => {
          if (!row.hasAccount) {
            return <Tag color="default">Sin cuenta</Tag>;
          }
          const PLAN_LABELS = {
            free: { label: "Free", color: "blue" },
            standard: { label: "Educa-Estandar", color: "green" },
            creator: { label: "Educa-Creator", color: "purple" },
            enterprise: { label: "Educa-Centro", color: "gold" },
          };
          const p = PLAN_LABELS[plan];
          return p ? (
            <Tag color={p.color}>{p.label}</Tag>
          ) : (
            <Tag color="default">{plan || "—"}</Tag>
          );
        },
      },
      {
        title: "Profes",
        dataIndex: "teachersEstimate",
        key: "teachersEstimate",
        width: 80,
        align: "right",
        render: (v) =>
          v == null ? <span style={{ color: "#94a3b8" }}>—</span> : v,
      },
      {
        title: "Asignado",
        dataIndex: "assigned_to",
        key: "assigned_to",
        width: 180,
        render: (v, row) => (
          <Select
            value={v?._id || ""}
            options={assignOptions}
            size="small"
            style={{ width: "100%" }}
            onClick={(e) => e.stopPropagation()}
            onChange={(val) => handleAssignChange(row._id, val || null)}
          />
        ),
      },
      {
        title: "Estado",
        dataIndex: "status",
        key: "status",
        width: 180,
        render: (status, row) => (
          <Select
            value={status}
            options={STATUS_OPTIONS}
            size="small"
            style={{ width: "100%" }}
            onClick={(e) => e.stopPropagation()}
            onChange={(v) => handleStatusChange(row._id, v)}
            optionLabelProp="label"
            // Render del valor seleccionado como Tag con color.
            tagRender={undefined}
          />
        ),
      },
    ],
    [handleStatusChange, handleAssignChange, assignOptions],
  );

  return (
    <div className="page-leads">
      <div className="page-leads__total">
        {data.total} lead{data.total === 1 ? "" : "s"} en total
      </div>

      <div className="page-leads__filters">
        <Input
          allowClear
          placeholder="Buscar por email, nombre o centro"
          value={filters.search}
          prefix={<SearchOutlined />}
          onChange={(e) =>
            setFilters((f) => ({ ...f, search: e.target.value }))
          }
          onPressEnter={() => {
            setPage(1);
            fetchLeads();
          }}
          style={{ maxWidth: 320 }}
        />
        <Select
          allowClear
          placeholder="Vertical"
          options={VERTICAL_OPTIONS}
          value={filters.vertical}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, vertical: v }));
          }}
          style={{ minWidth: 140 }}
        />
        <Select
          allowClear
          placeholder="Estado"
          options={STATUS_OPTIONS}
          value={filters.status}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, status: v }));
          }}
          style={{ minWidth: 160 }}
        />
        <Select
          allowClear
          placeholder="Asignado"
          options={ASSIGNED_OPTIONS}
          value={filters.assigned_to}
          onChange={(v) => {
            setPage(1);
            setFilters((f) => ({ ...f, assigned_to: v }));
          }}
          style={{ minWidth: 140 }}
        />
        <RangePicker
          format="DD/MM/YYYY"
          value={
            filters.from && filters.to
              ? [dayjs(filters.from), dayjs(filters.to)]
              : null
          }
          onChange={(range) => {
            setPage(1);
            setFilters((f) => ({
              ...f,
              from: range?.[0]?.startOf("day").toISOString(),
              to: range?.[1]?.endOf("day").toISOString(),
            }));
          }}
        />
        <button
          type="button"
          className="page-leads__refresh"
          onClick={fetchLeads}
          aria-label="Refrescar"
        >
          <ReloadOutlined />
        </button>
      </div>

      <div className="page-leads__status-tabs">
        {Object.entries(STATUS_LABEL).map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={
              filters.status === k
                ? "page-leads__status-tab page-leads__status-tab--active"
                : "page-leads__status-tab"
            }
            onClick={() => {
              setPage(1);
              setFilters((f) => ({
                ...f,
                status: f.status === k ? undefined : k,
              }));
            }}
          >
            <Tag color={STATUS_COLOR[k]}>{label}</Tag>
            <span className="page-leads__status-count">
              {data.byStatus?.[k] ?? 0}
            </span>
          </button>
        ))}
      </div>

      <Table
        rowKey="_id"
        loading={loading}
        dataSource={data.items}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total: data.total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (p, ps) => {
            setPage(p);
            setPageSize(ps);
          },
        }}
        onRow={(record) => ({
          onClick: () => navigate(`/admin/leads/${record._id}`),
          style: { cursor: "pointer" },
        })}
      />
    </div>
  );
}
